import type { PlayerProgress, StudentKP, Subject } from '../../shared/types';
import { findOrCreateKP, recordAttempt } from '../../shared/student-kp';
import { deriveQuestionId, recordMistake } from '../../shared/mistake-log';
import type { ExtractedItem } from './ocr-client';

export type ParentDecision = 'correct' | 'wrong' | 'careless' | 'discard';

export interface PromotionInput {
  item: ExtractedItem;
  decision: ParentDecision;
}

export interface PromotionResult {
  promoted: number;
  discarded: number;
  /** How many wrong/careless items were bridged into the 錯題本 (progress.mistakes). */
  mistakesAdded: number;
  studentKPsDelta: StudentKP[]; // KPs affected, snapshot for QR sync
}

/**
 * Resolve an {@link ExtractedItem}'s `correctAnswer` into a human-readable
 * value for the 錯題本 snapshot.
 *
 * For `mcq` the OCR stores `correctAnswer` as a 0-based **index** into
 * `options`; the mistake-book renders `String(entry.correctAnswer)` verbatim,
 * so we must resolve the index to the actual option text here — otherwise the
 * kid would see「正確答案:0」instead of the real choice. `fill` items already
 * carry the literal answer string.
 */
function resolveCorrectAnswer(item: ExtractedItem): string | number {
  if (item.kind === 'mcq' && typeof item.correctAnswer === 'number') {
    return item.options?.[item.correctAnswer] ?? item.correctAnswer;
  }
  return item.correctAnswer;
}

export function promoteApprovedItems(
  progress: PlayerProgress,
  inputs: PromotionInput[],
  nowISO: string = new Date().toISOString(),
): PromotionResult {
  let promoted = 0, discarded = 0, mistakesAdded = 0;
  const affectedKPIds = new Set<string>();
  // Defensive: legacy/migrated saves may lack the mistakes array.
  if (!Array.isArray(progress.mistakes)) progress.mistakes = [];

  for (const { item, decision } of inputs) {
    if (decision === 'discard') { discarded++; continue; }

    // 1) Leitner 弱點系統 (unchanged behaviour).
    const kp = findOrCreateKP(progress, item.kp, item.subject as Subject, 'exam-upload');
    recordAttempt(kp, decision, nowISO);
    affectedKPIds.add(kp.kpId);
    promoted++;

    // 2) BRIDGE → 錯題本. Only 「錯」 and 「粗心」 land in the mistake book;
    //    「對」 must not. recordMistake() handles 24h dedupe (same questionId →
    //    timestamp bump, no duplicate row) + FIFO cap, so re-photographing the
    //    same paper won't flood the book.
    if (decision === 'wrong' || decision === 'careless') {
      const before = progress.mistakes.length;
      const questionId = deriveQuestionId(item.prompt);
      recordMistake(progress, {
        questionId,
        kp: item.kp,
        subject: item.subject as Subject,
        prompt: item.prompt,
        correctAnswer: resolveCorrectAnswer(item),
        // 自動批改後 OCR 已讀到小孩手寫的答案 → 存進錯題本(空字串/未讀到則 null)。
        userAnswer: item.studentAnswer && item.studentAnswer.trim() !== '' ? item.studentAnswer : null,
        // OCR 自動批改帶回的「為什麼錯 / 怎麼算」說明 → 讓錯題本能教(空字串視同無)。
        explanation: item.explanation && item.explanation.trim() !== '' ? item.explanation.trim() : undefined,
      });
      // Tag careless items so the 錯題本 shows the「粗心」badge immediately.
      if (decision === 'careless') {
        const entry = progress.mistakes.find((m) => m.questionId === questionId);
        if (entry) entry.errorType = 'careless';
      }
      if (progress.mistakes.length > before) mistakesAdded++;
    }
  }
  const studentKPsDelta = (progress.studentKPs ?? []).filter(k => affectedKPIds.has(k.kpId));
  return { promoted, discarded, mistakesAdded, studentKPsDelta };
}
